import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { festivalService } from '../../services/festivalService';
import { languageService } from '../../services/languageService';
import MediaUpload from './MediaUpload';
import { FaTimes, FaCalendar, FaGlobe, FaImage, FaLanguage, FaMapMarkerAlt } from 'react-icons/fa';
// Using Tailwind CSS classes instead of external CSS

// Major cities in India
const INDIAN_CITIES = [
  'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Ahmedabad', 'Chennai',
  'Kolkata', 'Surat', 'Pune', 'Jaipur', 'Lucknow', 'Kanpur',
  'Nagpur', 'Indore', 'Thane', 'Bhopal', 'Visakhapatnam', 'Pimpri-Chinchwad',
  'Patna', 'Vadodara', 'Ghaziabad', 'Ludhiana', 'Agra', 'Nashik',
  'Faridabad', 'Meerut', 'Rajkot', 'Kalyan-Dombivali', 'Vasai-Virar', 'Varanasi',
  'Srinagar', 'Aurangabad', 'Dhanbad', 'Amritsar', 'Navi Mumbai', 'Allahabad',
  'Ranchi', 'Howrah', 'Coimbatore', 'Jabalpur', 'Gwalior', 'Vijayawada',
  'Jodhpur', 'Madurai', 'Raipur', 'Kota', 'Chandigarh', 'Guwahati',
  'Solapur', 'Hubli-Dharwad', 'Mysore', 'Tiruchirappalli', 'Bareilly', 'Aligarh',
  'Tiruppur', 'Moradabad', 'Jalandhar', 'Bhubaneswar', 'Salem', 'Warangal',
  'Guntur', 'Bhiwandi', 'Saharanpur', 'Gorakhpur', 'Bikaner', 'Amravati',
  'Noida', 'Jamshedpur', 'Bhilai', 'Cuttack', 'Firozabad', 'Kochi',
  'Nellore', 'Bhavnagar', 'Dehradun', 'Durgapur', 'Asansol', 'Rourkela',
  'Nanded', 'Kolhapur', 'Ajmer', 'Akola', 'Gulbarga', 'Jamnagar',
  'Ujjain', 'Loni', 'Siliguri', 'Jhansi', 'Ulhasnagar', 'Jammu',
  'Sangli-Miraj & Kupwad', 'Mangalore', 'Erode', 'Belgaum', 'Ambattur', 'Tirunelveli',
  'Malegaon', 'Gaya', 'Jalgaon', 'Udaipur', 'Maheshtala'
].sort();

const FestivalForm = ({ festival, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [formData, setFormData] = useState({
    name: {},
    description: {},
    date: '',
    languages: [],
    category: 'general',
    city: {},
    images: [],
    videos: [],
    isActive: true
  });

  useEffect(() => {
    fetchLanguages();
  }, []);

  useEffect(() => {
    if (festival) {
      console.log('=== FestivalForm useEffect ===');
      console.log('Received festival:', festival);
      console.log('Festival.name:', festival.name);
      console.log('Festival.name type:', typeof festival.name);
      console.log('Festival.name instanceof Map:', festival.name instanceof Map);
      console.log('Festival.description:', festival.description);

      // Convert Map to object if needed
      // When data comes from MongoDB via JSON, Maps are already plain objects (flattenMaps: true)
      let nameObj = festival.name || {};
      let descObj = festival.description || {};
      let cityObj = festival.city || {};

      // If it's a Map (unlikely from API but possible in some cases)
      if (festival.name instanceof Map) {
        console.log('Converting name Map to object');
        nameObj = Object.fromEntries(festival.name);
      }
      if (festival.description instanceof Map) {
        console.log('Converting description Map to object');
        descObj = Object.fromEntries(festival.description);
      }
      if (festival.city instanceof Map) {
        console.log('Converting city Map to object');
        cityObj = Object.fromEntries(festival.city);
      }

      // Validate city object - if it has numeric keys (character-split format), reset it
      if (cityObj && typeof cityObj === 'object') {
        const cityKeys = Object.keys(cityObj);
        const hasNumericKeys = cityKeys.length > 0 && cityKeys.every(key => !isNaN(key));
        if (hasNumericKeys) {
          console.log('Invalid city format detected (character-split), resetting to empty');
          cityObj = {};
        }
      }

      console.log('Final nameObj:', nameObj);
      console.log('Final descObj:', descObj);
      console.log('Final cityObj:', cityObj);

      setFormData({
        name: nameObj,
        description: descObj,
        date: festival.date ? new Date(festival.date).toISOString().split('T')[0] : '',
        languages: festival.languages || [],
        category: festival.category || 'general',
        city: cityObj,
        images: festival.images || [],
        videos: festival.videos || [],
        isActive: festival.isActive !== undefined ? festival.isActive : true
      });
    }
  }, [festival]);

  const fetchLanguages = async () => {
    try {
      const response = await languageService.getLanguages();
      setAvailableLanguages(response.data);
    } catch (error) {
      console.error('Error fetching languages:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (festival) {
        await festivalService.updateFestival(festival._id, formData);
      } else {
        await festivalService.createFestival(formData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving story:', error);
      alert('Failed to save story');
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (lang, value) => {
    setFormData(prev => ({
      ...prev,
      name: { ...prev.name, [lang]: value }
    }));
  };

  const handleDescriptionChange = (lang, value) => {
    setFormData(prev => ({
      ...prev,
      description: { ...prev.description, [lang]: value }
    }));
  };

  const handleCityChange = (lang, value) => {
    setFormData(prev => ({
      ...prev,
      city: { ...prev.city, [lang]: value }
    }));
  };

  const handleLanguageToggle = (langCode) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(langCode)
        ? prev.languages.filter(l => l !== langCode)
        : [...prev.languages, langCode]
    }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
      <div className="fixed left-[50%] top-[50%] z-50 flex flex-col w-full max-w-7xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold leading-none tracking-tight">{festival ? 'Edit Story' : 'Create New Story'}</h3>
            <p className="text-sm text-muted-foreground mt-1">Configure story details and multilingual content</p>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>
            <FaTimes className="h-12 w-12" />
          </button>
        </div>

        <form onSubmit={handleSubmit} id="festival-form" className="flex-1 overflow-y-auto">
          <div className="space-y-6 p-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <FaLanguage className="h-5 w-5 text-primary" />
                <div>
                  <h4 className="text-base font-semibold text-foreground">Story Names</h4>
                  <p className="text-xs text-muted-foreground">Enter story names in multiple languages</p>
                </div>
              </div>
              <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {availableLanguages.map((lang, index) => (
                  <div key={lang.code} className="relative">
                    <div className="card p-4 hover:shadow-sm transition-shadow border border-border">
                      <label className="block mb-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-foreground">
                            {lang.name}{index === 0 && <span className="text-red-500 ml-1">*</span>}
                          </span>
                          <span className="text-xs text-muted-foreground font-medium">{lang.nativeName}</span>
                        </div>
                      </label>
                      <input
                        type="text"
                        className="input w-full"
                        value={formData.name[lang.code] || ''}
                        onChange={(e) => handleNameChange(lang.code, e.target.value)}
                        placeholder={`Enter name in ${lang.name}`}
                        required={index === 0}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <FaCalendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <h4 className="text-sm font-medium">Story Date & Category</h4>
                  <p className="text-xs text-muted-foreground">Select the story date and category</p>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="label">Date *</label>
                  <input
                    type="date"
                    className="input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="label">Category</label>
                  <select
                    className="select"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="general">General</option>
                    <option value="religious">Religious</option>
                    <option value="cultural">Cultural</option>
                    <option value="national">National</option>
                    <option value="regional">Regional</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <FaMapMarkerAlt className="h-5 w-5 text-primary" />
                <div>
                  <h4 className="text-base font-semibold text-foreground">City/Location</h4>
                  <p className="text-xs text-muted-foreground">Enter city or location names in multiple languages (Optional)</p>
                </div>
              </div>
              <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {availableLanguages.map((lang) => (
                  <div key={lang.code} className="relative">
                    <div className="card p-4 hover:shadow-sm transition-shadow border border-border">
                      <label className="block mb-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-foreground">{lang.name}</span>
                          <span className="text-xs text-muted-foreground font-medium">{lang.nativeName}</span>
                        </div>
                      </label>
                      <input
                        type="text"
                        className="input w-full"
                        value={formData.city[lang.code] || ''}
                        onChange={(e) => handleCityChange(lang.code, e.target.value)}
                        placeholder={`Enter city in ${lang.name}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <FaLanguage className="h-5 w-5 text-primary" />
                <div>
                  <h4 className="text-base font-semibold text-foreground">Story Descriptions</h4>
                  <p className="text-xs text-muted-foreground">Provide detailed descriptions in multiple languages</p>
                </div>
              </div>
              <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
                {availableLanguages.map((lang) => (
                  <div key={lang.code} className="relative">
                    <div className="card p-4 hover:shadow-sm transition-shadow border border-border">
                      <label className="block mb-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-foreground">{lang.name}</span>
                          <span className="text-xs text-muted-foreground font-medium">{lang.nativeName}</span>
                        </div>
                      </label>
                      <textarea
                        className="textarea w-full"
                        rows="3"
                        value={formData.description[lang.code] || ''}
                        onChange={(e) => handleDescriptionChange(lang.code, e.target.value)}
                        placeholder={`Enter description in ${lang.name}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <FaGlobe className="h-4 w-4 text-muted-foreground" />
                <div>
                  <h4 className="text-sm font-medium">Supported Languages</h4>
                  <p className="text-xs text-muted-foreground">Select languages for this festival</p>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {availableLanguages.map((lang) => (
                  <div key={lang.code} className="card p-3 hover:shadow-sm transition-shadow">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-border focus:ring-ring focus:ring-offset-0"
                        checked={formData.languages.includes(lang.code)}
                        onChange={() => handleLanguageToggle(lang.code)}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{lang.nativeName}</div>
                        <div className="text-xs text-muted-foreground">({lang.name})</div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <FaImage className="h-4 w-4 text-muted-foreground" />
                <div>
                  <h4 className="text-sm font-medium">Media Content</h4>
                  <p className="text-xs text-muted-foreground">Upload images and videos for this festival</p>
                </div>
              </div>
              <MediaUpload
                images={formData.images}
                videos={formData.videos}
                festivalId={festival?._id}
                onMediaUpdate={(images, videos) => setFormData({ ...formData, images, videos })}
              />
            </div>
          </div>
        </form>
          
        <div className="flex justify-end space-x-3 p-6 border-t border-border bg-muted/10">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn btn-default" disabled={loading} form="festival-form">
            {loading ? 'Saving...' : (festival ? 'Update Festival' : 'Create Festival')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FestivalForm;
